import React, { FC, useContext, useEffect, useState } from 'react';
import Shift from './Shift';
import { Dayjs } from 'dayjs';
import { createOpenShift, IShift, removeShifts } from './shiftApi';
import { getInterpolatedDates, toIsoDate } from 'utils/date';
import {
  afterPeriodChangesPossible,
  CourierCollection,
  handleError,
  mapCourierIdsToShiftStatus,
  mapShiftToColourGroup,
  SHIFT_COLOUR_ORDER,
  ShiftsByDate,
} from './utils';
import './ShiftCalendar.scss';
import { partition } from 'utils/array';
import { ShiftStatusType } from 'const/ShiftConst';
import SuitcaseIcon from 'assets/svg/SuitcaseIcon';
import AssignmentIcon from 'assets/svg/AssignmentIcon';
import BlackMinusIcon from '../../assets/svg/BlackMinusIcon';
import { CourierJson } from 'types/bundleTypes';
import CourierPickerModal from './modals/CourierPickerModal';
import { CourierWorkModal } from './modals/CourierWorkModal';
import CourierContactModal from './modals/CourierContactModal';
import {
  CalendarCustomButton,
  Separator,
  TransportOverview,
} from './smallerComponents';
import { TooltipComponent } from '../../ReusableComponents/Tooltip/TooltipComponent';
import { ShiftPlannerContext } from './ShiftPlanner';
import Swal from 'sweetalert2';

const ShiftCalendar: FC<{
  displayedCouriers: CourierCollection;
  displayedShifts: ShiftsByDate;
  startDate: Dayjs;
  endDate: Dayjs;
}> = ({ displayedCouriers, displayedShifts, startDate, endDate }) => {
  const displayDates = getInterpolatedDates(startDate, endDate);

  return (
    <div className="newShiftCalendar">
      {displayDates.map(date => {
        const shiftList = displayedShifts[toIsoDate(date)] ?? [];
        return (
          <CalendarDay
            key={date.toString()}
            date={date}
            couriers={displayedCouriers}
            shiftList={shiftList}
          />
        );
      })}
    </div>
  );
};

const CalendarDay: FC<{
  date: Dayjs;
  couriers: CourierCollection;
  shiftList: IShift[];
}> = ({ date, couriers, shiftList }) => {
  const [initialShifts, setInitialShifts] = useState(shiftList);
  const [showModal, setShowModal] = useState<boolean>(false);

  const wishList = initialShifts
    .filter(shift => shift.status === ShiftStatusType.WISH)
    .map(shift => shift.courierId);

  const bookedCouriers = initialShifts.map(shift => shift.courierId);
  const [availableCouriers, unavailableCouriers] = partition(
    couriers.list.filter(courier => !courier.isLocked),
    courier => !bookedCouriers.includes(courier.id)
  );

  const removeAllShifts = () => {
    const displayDate = `${date.format('dddd')}, ${date.format('DD.MM')}`;
    Swal.fire({
      title: `Remove all shifts for ${displayDate}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.value) {
        const shiftsToRemove = initialShifts
          .filter(shift => bookedCouriers.includes(shift.courierId))
          .map(shift => shift.id);
        removeShifts(shiftsToRemove).catch(handleError);
      }
    });
  };

  useEffect(() => {
    setInitialShifts(shiftList);
  }, [shiftList]);

  useEffect(() => {
    const groupedShifts: Record<string, IShift[]> = shiftList.reduce(
      (result, shift) => {
        const shiftColorGroup = mapShiftToColourGroup(shift.status);
        if (!result[shiftColorGroup]) {
          result[shiftColorGroup] = [];
        }
        result[shiftColorGroup].push(shift);
        return result;
      },
      {}
    );
    if (couriers?.byId && Object.keys(couriers?.byId).length) {
      try {
        Object.entries(groupedShifts)
          .filter(entry => entry[0] !== 'blue')
          .forEach(([color, shifts]) => {
            groupedShifts[color] = shifts.sort((shiftA, shiftB) => {
              return couriers.byId[shiftA.courierId].fullName.localeCompare(
                couriers.byId[shiftB.courierId].fullName
              );
            });
          });
      } catch (err) {
        console.error(err);
      }

      if (shiftList.length) {
        setInitialShifts([
          ...SHIFT_COLOUR_ORDER.flatMap(
            color => groupedShifts[color] ?? undefined
          ).filter(shift => !!shift),
        ]);
      }
    }
  }, [couriers, shiftList]);

  return (
    <div key={date.toString()} className="newShiftCalendar__day">
      {showModal && (
        <CourierPickerModal
          date={date}
          wishList={wishList}
          couriers={availableCouriers}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
      <DayHeader
        shifts={shiftList}
        allCouriers={couriers}
        assignedCouriers={unavailableCouriers}
        date={date}
      />
      <div className="newShiftCalendar__day__shifts">
        {bookedCouriers.length > 0 && (
          <div
            className="newShiftCalendar__customButton"
            onClick={removeAllShifts}
          >
            <BlackMinusIcon className="newShiftCalendar__customButton__icon" />
            <p>Remove all shifts</p>
          </div>
        )}
        <CalendarCustomButton
          text="Open shift"
          onClick={() => createOpenShift(date).catch(handleError)}
        />
        <CalendarCustomButton
          text="Multiple shifts"
          onClick={() => setShowModal(true)}
        />
        {initialShifts.map(shift => (
          <Shift
            key={shift.id}
            date={date}
            shiftData={shift}
            wishList={wishList}
            courier={couriers.byId[shift.courierId]}
            couriers={availableCouriers}
          />
        ))}
      </div>
    </div>
  );
};

const DayHeader: FC<{
  shifts: IShift[];
  allCouriers: CourierCollection;
  assignedCouriers: CourierJson[];
  date: Dayjs;
}> = ({ shifts, allCouriers, assignedCouriers, date }) => {
  const [showWorkModal, setShowWorkModal] = useState<boolean>(false);
  const [showContactModal, setShowContactModal] = useState<boolean>(false);

  const { daysUntilCalculation } = useContext(ShiftPlannerContext);

  const { booked, requested, canceled, declined, dispatchDeclined } =
    mapCourierIdsToShiftStatus(shifts);

  const courierShiftMap: { [courierId: number]: IShift } = {};
  shifts.forEach(shift => (courierShiftMap[shift.courierId] = shift));
  const bookedCouriers = allCouriers.list.filter(courier =>
    booked.includes(courier.id)
  );

  return (
    <div className="newShiftCalendar__day__header">
      {showWorkModal && (
        <CourierWorkModal
          date={date}
          courierShiftMap={courierShiftMap}
          couriers={bookedCouriers}
          showModal={showWorkModal}
          setShowModal={setShowWorkModal}
        />
      )}
      {showContactModal && (
        <CourierContactModal
          date={date}
          courierShiftMap={courierShiftMap}
          couriers={assignedCouriers}
          showModal={showContactModal}
          setShowModal={setShowContactModal}
        />
      )}
      <div>
        <h1 className="newShiftCalendar__day__header__date">
          {`${date.format('dddd')} ${date.format('DD.MM')}`}
        </h1>
      </div>
      <div className="newShiftCalendar__day__header__counts">
        <TooltipComponent id="booked-couriers-count" title="Booked">
          <span className="newShiftCalendar__day__header__counts__property">
            B
          </span>
          <span className="newShiftCalendar__day__header__counts__value">
            {booked.length}
          </span>
        </TooltipComponent>
        <hr />
        <TooltipComponent id="requested-couriers-count" title="Requested">
          <span className="newShiftCalendar__day__header__counts__property">
            R
          </span>
          <span className="newShiftCalendar__day__header__counts__value">
            {requested.length}
          </span>
        </TooltipComponent>
        <hr />
        <TooltipComponent id="declined-couriers-count" title="Declined">
          <span className="newShiftCalendar__day__header__counts__property">
            D
          </span>
          <span className="newShiftCalendar__day__header__counts__value">
            {declined.length}
          </span>
        </TooltipComponent>
        <hr />
        <TooltipComponent id="canceled-couriers-count" title="Canceled">
          <span className="newShiftCalendar__day__header__counts__property">
            C
          </span>
          <span className="newShiftCalendar__day__header__counts__value">
            {canceled.length}
          </span>
        </TooltipComponent>
        <hr />
        <TooltipComponent
          id="canceled-couriers-count"
          title="Dispatch declined"
        >
          <span className="newShiftCalendar__day__header__counts__property">
            DD
          </span>
          <span className="newShiftCalendar__day__header__counts__value">
            {dispatchDeclined.length}
          </span>
        </TooltipComponent>
      </div>
      <Separator />
      <TransportOverview
        className="newShiftCalendar__day__header__vehicles"
        couriers={allCouriers}
        shifts={shifts}
      />
      <Separator />
      <div className="newShiftCalendar__day__header__buttons">
        {afterPeriodChangesPossible(date, daysUntilCalculation) ? (
          <TooltipComponent
            id={date.day().toString()}
            title={'Changes for this day are no longer possible'}
          >
            <button
              className="newShiftCalendar__day__header__buttons__extra-work__disabled"
              disabled={true}
            >
              <SuitcaseIcon />
              <p>Add work</p>
            </button>
          </TooltipComponent>
        ) : (
          <button
            className="newShiftCalendar__day__header__buttons__extra-work"
            onClick={() => setShowWorkModal(true)}
          >
            <SuitcaseIcon />
            <p>Add work</p>
          </button>
        )}
        <button
          className="newShiftCalendar__day__header__buttons__contact"
          onClick={() => setShowContactModal(true)}
        >
          <AssignmentIcon />
          <p>Contact</p>
        </button>
      </div>
      <Separator />
    </div>
  );
};

export default ShiftCalendar;
